(() => {
  const ROOT_CLASS = 'kex-smooth-details';
  const FAQ_DETAILS = [
    '.cc-faq details',
    '.kexpdp-faq__item',
    '.kexfaq__item',
    '.kex-course-faq__item',
    '.kexdt__faq details',
    '.kexv-faq',
    '.kexpumpkin__faq-item',
    '.kexcupsc__faq-card',
    '.kexsup__faq-card'
  ].join(',');
  const EXCLUDED_DETAILS = [
    '.main-menu__disclosure',
    '.details-modal',
    '.kexbiz__faq-item',
    '[data-kex-sampler-dropdown]',
    '[data-kex-no-smooth]'
  ].join(',');
  const EXCLUDED_PARENTS = [
    'details-disclosure',
    '.main-nav',
    '.child-nav',
    '[data-kexs-drawer]'
  ].join(',');

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SmoothDisclosure {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector(':scope > summary');
      this.animation = null;
      this.contentAnimations = [];
      this.isClosing = false;
      this.isOpening = false;
      this.isFastFaq = details.classList.contains('kexbiz__faq-item');
      this.isFaq = details.matches(FAQ_DETAILS);

      if (!this.summary) return;

      if (this.isFaq) details.open = false;
      details.classList.add(ROOT_CLASS);
      this.syncState();
      this.summary.addEventListener('click', this.onClick.bind(this));
    }

    get content() {
      return [...this.details.children].filter((child) => child !== this.summary);
    }

    syncState() {
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.details.classList.toggle('is-open', this.details.open);
    }

    cancelAnimations() {
      this.animation?.cancel();
      this.contentAnimations.forEach((animation) => animation.cancel());
      this.animation = null;
      this.contentAnimations = [];
    }

    onClick(event) {
      if (prefersReducedMotion()) return;
      if (event.target.closest('a, button, input, select, textarea') && event.target !== this.summary) return;

      event.preventDefault();
      this.details.style.overflow = 'clip';

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isOpening || this.details.open) {
        this.close();
      }
    }

    open() {
      this.cancelAnimations();
      this.details.classList.remove('is-closing');
      this.details.classList.add('is-opening');
      this.isClosing = false;
      this.isOpening = true;

      const startHeight = `${this.details.offsetHeight}px`;
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      const endHeight = `${this.details.scrollHeight}px`;

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.isFastFaq ? 190 : 420, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );

      this.contentAnimations = this.content.map((element) => element.animate(
        [
          { opacity: 0, transform: 'translateY(-8px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ],
        { duration: this.isFastFaq ? 140 : 300, delay: this.isFastFaq ? 0 : 55, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' }
      ));

      this.animation.onfinish = () => this.finish(true);
      this.animation.oncancel = () => { this.isOpening = false; };
    }

    close() {
      this.cancelAnimations();
      this.details.classList.remove('is-opening');
      this.details.classList.add('is-closing');
      this.isClosing = true;
      this.isOpening = false;
      this.summary.setAttribute('aria-expanded', 'false');

      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.isFastFaq ? 160 : 330, easing: 'cubic-bezier(.4, 0, .2, 1)' }
      );

      this.contentAnimations = this.content.map((element) => element.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(-6px)' }
        ],
        { duration: this.isFastFaq ? 110 : 190, easing: 'cubic-bezier(.4, 0, 1, 1)', fill: 'both' }
      ));

      this.animation.onfinish = () => this.finish(false);
      this.animation.oncancel = () => { this.isClosing = false; };
    }

    finish(open) {
      this.details.open = open;
      this.contentAnimations.forEach((animation) => animation.cancel());
      this.details.style.removeProperty('overflow');
      this.details.classList.remove('is-opening', 'is-closing');
      this.details.classList.toggle('is-open', open);
      this.summary.setAttribute('aria-expanded', String(open));
      this.animation = null;
      this.contentAnimations = [];
      this.isClosing = false;
      this.isOpening = false;
    }
  }

  const eligible = (details) => (
    details instanceof HTMLDetailsElement
    && !details.classList.contains(ROOT_CLASS)
    && !details.matches(EXCLUDED_DETAILS)
    && !details.closest(EXCLUDED_PARENTS)
  );

  const initialize = (root = document) => {
    if (root instanceof HTMLDetailsElement && eligible(root)) new SmoothDisclosure(root);
    root.querySelectorAll?.('details').forEach((details) => {
      if (eligible(details)) new SmoothDisclosure(details);
    });
  };

  initialize();

  new MutationObserver((mutations) => {
    mutations.forEach(({ addedNodes }) => {
      addedNodes.forEach((node) => {
        if (node instanceof Element) initialize(node);
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
